import { addDays } from '~/calculations/calendar'
import { type CalendarDate, type IsoTimestamp, unwrapDate } from '~/types/dates'
import {
  type DomainGoalId,
  newId,
  type ProjectId,
  type ScheduledSessionId,
  type TrainingWeekId,
} from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type RecordEnvelope, type ValidationIssue, type ValidationIssues } from './common'
import type { FitnessDomain } from './fitnessDomain'
import { DB_SCHEMA_VERSION } from './schemaVersion'
import { sessionShapeIssues } from './trainingSchedule'
import { type Weekday, weekdayOffset } from './weekday'

/**
 * The materialised week — the execution layer (D-105 §2, §4, §6).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS LAYER NEVER WRITES TO THE SCHEDULE, AND THAT IS THE ARCHITECTURE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  The recurring schedule on a domain goal is **plan data**, effective from the
 *  start of next week. This record is **what that plan became** for one week,
 *  frozen from the schedule in force when the week began. Completing, moving,
 *  skipping and adding a one-off touch only this record.
 *
 *  **Do not let an instance action write back to the schedule.** A move on
 *  Wednesday is not a plan change, and a plan change on Wednesday must not move
 *  anything already on screen — D-099 Amendment 2 is preserved exactly, not
 *  weakened, and that is what makes both true at once.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A WEEK THE USER NEVER SAW DOES NOT EXIST
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Materialisation is lazy, on first read of the current week, and **only the
 *  current week is ever materialised** (D-105 §6). If the app is not opened all
 *  week, no record exists — which is correct and non-punitive: there is nothing
 *  to resolve and nothing was missed. **A week nobody saw must not be
 *  manufactured retroactively so that it can be reported as empty.**
 *
 *  Sessions here are value snapshots. A later schedule, pause, priority or
 *  domain edit never rewrites an existing week.
 */

/**
 * One rescheduling, kept apart from the outcome.
 *
 * **A move must not resolve a session** (D-105 §4), and separate fields are how
 * that is made structural rather than remembered: collapsing rescheduling
 * history into the outcome would make *"I'll do it Thursday"* indistinguishable
 * from *"I did it"*.
 *
 * Moves are **within the current week only**. A cross-week move is deliberately
 * unbuilt — it would be a plan change wearing an instance's clothes.
 */
export interface SessionMove {
  readonly kind: 'moved'
  readonly from: CalendarDate
  readonly to: CalendarDate
  readonly at: IsoTimestamp
}

/**
 * How a session ended, when it ended at all.
 *
 * **`undefined` is a fourth state and it is the important one: *not recorded*.**
 * Never "missed", never "skipped", never "failed". An unresolved session at the
 * week boundary keeps it — no lapse event, no synthetic resolution, no backlog
 * crossing into the new week (D-105 §5). This is D-096's rule in its newest
 * costume: missing evidence is omitted from a count rather than counted against
 * the user.
 *
 * A `reason` on a skip is free text the user wrote, so it gates on
 * `includeNotes` in every export (D-105 §12).
 */
export type SessionOutcome =
  | { readonly kind: 'completed'; readonly at: IsoTimestamp }
  | { readonly kind: 'skipped'; readonly at: IsoTimestamp; readonly reason?: string }

/**
 * Where a session in a materialised week came from.
 *
 * A one-off was added for this week alone. It counts as recorded training
 * evidence, is labelled as added, does not recur, and is **never written back to
 * the recurring schedule** (D-105 §4).
 */
export const SESSION_ORIGINS = ['schedule', 'oneOff'] as const

export type SessionOrigin = (typeof SESSION_ORIGINS)[number]

/**
 * One session inside a materialised week.
 *
 * `plannedDate` is immutable and generated from the schedule; `scheduledDate` is
 * where it stands after zero or more moves. Both are kept because "which day was
 * this meant to be?" and "which day is it now?" are different questions, and the
 * export redacts them together (D-105 §12).
 *
 * `name` and `expectedMinutes` are **snapshots**, not references. Renaming a
 * recurring session next week must not silently rewrite what last week said.
 */
export interface MaterialisedSession {
  readonly id: string
  /**
   * The recurring session this was materialised from.
   *
   * Absent on a one-off, which by definition has no recurring parent. Present
   * ids stay stable across schedule edits, so a session that was renamed is
   * still recognisably the same session (D-105 §7).
   */
  readonly sourceSessionId?: ScheduledSessionId
  readonly domain: FitnessDomain
  readonly name: string
  readonly expectedMinutes?: number
  readonly origin: SessionOrigin
  readonly plannedDate: CalendarDate
  readonly scheduledDate: CalendarDate
  readonly changes: readonly SessionMove[]
  readonly outcome?: SessionOutcome
  readonly createdAt: IsoTimestamp
}

/**
 * One week, as it was actually planned and executed.
 *
 * **The key is an opaque id with `weekStartDate` and `projectId` as indexes, not
 * the week start date as a natural key** (D-105 §6). Two cases make the natural
 * key unsafe: a mid-project change of `weekStartDay`, and a project started
 * mid-week — either produces two records claiming the same day. `weeklyReviews`
 * already has exactly this shape, so this follows it rather than inventing a
 * scheme.
 *
 * `weekStartDay` is stored rather than read back from settings, and
 * `weekEndDate` is stored rather than computed. **A materialised week is
 * authoritative for its entire inclusive span** (D-105 §8): historical weeks keep
 * their recorded dates and never consult the current setting, which is what makes
 * the pending-change setting safe.
 */
export interface TrainingWeekRecord extends RecordEnvelope {
  readonly id: TrainingWeekId
  readonly projectId: ProjectId
  readonly weekStartDate: CalendarDate
  /** Inclusive. The last day this record is authoritative for. */
  readonly weekEndDate: CalendarDate
  readonly weekStartDay: Weekday
  /**
   * The domain goals this week was materialised from.
   *
   * Recorded so a finished week can say which plan produced it even after a
   * domain is paused or ended — the same reasoning as the review's immutable
   * snapshot (D-046).
   */
  readonly sourceGoalIds: readonly DomainGoalId[]
  readonly sessions: readonly MaterialisedSession[]
  readonly materialisedAt: IsoTimestamp
}

/** Whether this session has been resolved either way. */
export function isResolved(session: MaterialisedSession): boolean {
  return session.outcome !== undefined
}

/* ── Building a week ─────────────────────────────────────────────────────── */

const DAYS_IN_WEEK = 7

/** One recurring session, ready to be frozen into a week. */
export interface ScheduledSessionSeed {
  readonly sourceSessionId: ScheduledSessionId
  readonly domain: FitnessDomain
  readonly name: string
  readonly expectedMinutes?: number
  readonly weekday: Weekday
}

export interface TrainingWeekInput {
  readonly projectId: ProjectId
  readonly weekStartDate: CalendarDate
  readonly weekStartDay: Weekday
  readonly sourceGoalIds: readonly DomainGoalId[]
  readonly sessions: readonly ScheduledSessionSeed[]
  /**
   * The first day this project existed, when it began inside this week.
   *
   * A project started on Wednesday must not materialise Monday's and Tuesday's
   * sessions: they would arrive already unresolved, from before the plan
   * existed, and the user would open the app to work they were never asked to
   * do. Omitted when the project began on or before the week start, which is
   * every ordinary week.
   */
  readonly notBefore?: CalendarDate
}

/**
 * Freeze the schedule in force into one week (D-105 §6).
 *
 * **Every session is a value snapshot**, so a later rename, pause or priority
 * change never rewrites this record. `plannedDate` is computed once, here, and is
 * immutable afterwards: it is the answer to *"which day was this meant to be?"*,
 * which stays a real question after any number of moves.
 *
 * The `weekStartDay` is stored alongside the dates rather than looked up later,
 * because a materialised week is authoritative for its whole span and must never
 * consult the current setting (D-105 §8).
 */
export function buildTrainingWeek(
  input: TrainingWeekInput,
  timestamp: IsoTimestamp,
): Result<TrainingWeekRecord, ValidationIssues> {
  const issues: ValidationIssue[] = []
  for (const seed of input.sessions) {
    issues.push(...sessionShapeIssues('sessions', seed.name, seed.expectedMinutes))
  }
  if (issues.length > 0) return err(issues)

  const weekEndDate = addDays(input.weekStartDate, DAYS_IN_WEEK - 1)
  const sessions = input.sessions
    .map((seed) => materialise(seed, input.weekStartDate, input.weekStartDay, timestamp))
    .filter((session) => startsOnOrAfter(session.plannedDate, input.notBefore))

  return ok({
    id: newId<'TrainingWeekId'>(),
    projectId: input.projectId,
    weekStartDate: input.weekStartDate,
    weekEndDate,
    weekStartDay: input.weekStartDay,
    sourceGoalIds: input.sourceGoalIds,
    sessions,
    materialisedAt: timestamp,
    createdAt: timestamp,
    updatedAt: timestamp,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}

function materialise(
  seed: ScheduledSessionSeed,
  weekStartDate: CalendarDate,
  weekStartDay: Weekday,
  timestamp: IsoTimestamp,
): MaterialisedSession {
  const date = addDays(weekStartDate, weekdayOffset(seed.weekday, weekStartDay))
  return {
    id: newId<'MaterialisedSessionId'>(),
    sourceSessionId: seed.sourceSessionId,
    domain: seed.domain,
    name: seed.name.trim(),
    ...(seed.expectedMinutes === undefined ? {} : { expectedMinutes: seed.expectedMinutes }),
    origin: 'schedule',
    plannedDate: date,
    scheduledDate: date,
    changes: [],
    createdAt: timestamp,
  }
}

function startsOnOrAfter(date: CalendarDate, boundary: CalendarDate | undefined): boolean {
  if (boundary === undefined) return true
  return unwrapDate(date) >= unwrapDate(boundary)
}

/* ── Reading a week ──────────────────────────────────────────────────────── */

export function isWithinWeek(week: TrainingWeekRecord, date: CalendarDate): boolean {
  const value = unwrapDate(date)
  return value >= unwrapDate(week.weekStartDate) && value <= unwrapDate(week.weekEndDate)
}

/** Everything standing on one day, resolved or not. */
export function sessionsOn(
  week: TrainingWeekRecord,
  date: CalendarDate,
): readonly MaterialisedSession[] {
  return week.sessions.filter((session) => unwrapDate(session.scheduledDate) === unwrapDate(date))
}

/**
 * Sessions whose day has passed with nothing recorded (D-105 §5).
 *
 * These are shown separately, as *"Still open from this week"* — **not as one of
 * today's tasks**, so "you're done for today" stays reachable while the screen
 * still says a session is open. Naming it *open* rather than *missed* or
 * *overdue* is the whole point: it is a state, not a judgement, and at the week
 * boundary it simply stops being actionable.
 */
export function openSessionsBefore(
  week: TrainingWeekRecord,
  date: CalendarDate,
): readonly MaterialisedSession[] {
  return week.sessions.filter(
    (session) => !isResolved(session) && unwrapDate(session.scheduledDate) < unwrapDate(date),
  )
}

/* ── Counting a week ─────────────────────────────────────────────────────── */

/**
 * What a week planned and what was completed (D-105 §3).
 *
 * **Two separate numbers, never a ratio.** `weekChecklist.ts` already keeps
 * `soFar` and `target` apart *"so that a renderer is never handed a ratio"*, and
 * the same reasoning applies here: a ratio invites a percentage and a percentage
 * is a score. Operational counts are permitted — *"2 completed, 1 skipped"* —
 * and this is the shape that makes the permitted thing easy and the forbidden
 * thing awkward.
 */
export interface TrainingSessionCounts {
  readonly planned: number
  readonly completed: number
}

/**
 * The week's totals.
 *
 * **A one-off raises `planned` as well as `completed`.** It was training the
 * user did and it is training they meant to do, so counting it only as completed
 * would push completed above planned — which `validateAdherenceCheck` refuses,
 * and rightly: D-066 reads completion as a fraction of what was planned.
 *
 * **A skipped session stays in `planned` and is not completed.** It was planned
 * and it did not happen, which is exactly what an explicit skip records. An
 * unresolved session is counted the same way — in `planned`, not in `completed`
 * — because *not recorded* contributes no evidence either way and is never
 * converted into a zero (D-105 §5).
 */
export function countTrainingSessions(week: TrainingWeekRecord): TrainingSessionCounts {
  return {
    planned: week.sessions.length,
    completed: week.sessions.filter((session) => session.outcome?.kind === 'completed').length,
  }
}

/** The same totals, split by the domain goal each session belongs to. */
export function countTrainingByDomain(
  week: TrainingWeekRecord,
): Readonly<Partial<Record<FitnessDomain, TrainingSessionCounts>>> {
  const counts: Partial<Record<FitnessDomain, TrainingSessionCounts>> = {}

  for (const session of week.sessions) {
    const running = counts[session.domain] ?? { planned: 0, completed: 0 }
    counts[session.domain] = {
      planned: running.planned + 1,
      completed: running.completed + (session.outcome?.kind === 'completed' ? 1 : 0),
    }
  }

  return counts
}

/* ── Changing a week ─────────────────────────────────────────────────────── */

function fail(message: string): Result<TrainingWeekRecord, ValidationIssues> {
  return err([issue('sessions', message)])
}

function replaceSession(
  week: TrainingWeekRecord,
  sessionId: string,
  change: (session: MaterialisedSession) => MaterialisedSession,
  timestamp: IsoTimestamp,
): TrainingWeekRecord {
  return {
    ...week,
    sessions: week.sessions.map((session) =>
      session.id === sessionId ? change(session) : session,
    ),
    updatedAt: timestamp,
  }
}

function findSession(week: TrainingWeekRecord, sessionId: string): MaterialisedSession | undefined {
  return week.sessions.find((session) => session.id === sessionId)
}

/**
 * Record an outcome, either way.
 *
 * **Re-recording is permitted while the week is open**, because a tap on the
 * wrong row is exactly the correction D-096's boundary already allows for a
 * nutrition day — and the service refuses everything once the covering review is
 * finalized, which is where that boundary lives. Recording the outcome a session
 * already has changes nothing and writes nothing, so the record does not gain a
 * new `updatedAt` for a tap that said what it already said.
 */
export function recordSessionOutcome(
  week: TrainingWeekRecord,
  sessionId: string,
  outcome: SessionOutcome,
  timestamp: IsoTimestamp,
): Result<TrainingWeekRecord, ValidationIssues> {
  const session = findSession(week, sessionId)
  if (session === undefined) return fail('That session is not part of this week.')
  if (sameOutcome(session.outcome, outcome)) return ok(week)

  return ok(replaceSession(week, sessionId, (current) => ({ ...current, outcome }), timestamp))
}

/**
 * Whether recording this would say what the session already says.
 *
 * The timestamp is deliberately not compared: re-tapping *completed* is the same
 * claim made twice, and treating a new `at` as a change would rewrite the moment
 * the user actually did the work. A skip's reason **is** compared, because
 * changing the reason is a real correction.
 */
function sameOutcome(current: SessionOutcome | undefined, next: SessionOutcome): boolean {
  if (current === undefined || current.kind !== next.kind) return false
  if (current.kind === 'skipped' && next.kind === 'skipped') {
    return current.reason === next.reason
  }
  return true
}

/**
 * Move a session to another day in the same week.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A MOVE MUST NOT RESOLVE A SESSION, AND THIS IS THE RULE MOST LIKELY TO BE
 *  GOT WRONG (D-105 §4)
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Moving resolves *today's obligation* and nothing else. `outcome` is not
 *  touched here and must never be: the session stays outstanding on its new
 *  date, and can be completed, skipped or moved again. Writing an outcome from
 *  this function would make *"I'll do it Thursday"* indistinguishable from
 *  *"I did it"* — which is the whole reason `changes` and `outcome` are separate
 *  fields rather than one status.
 *
 *  **A resolved session is refused rather than moved.** Its day is settled;
 *  moving it would be rewriting history rather than rescheduling work. Change the
 *  outcome first if the record is wrong.
 *
 *  **Moves stay inside the week** (D-105 §4). A cross-week move is deliberately
 *  unbuilt: it would be a plan change wearing an instance's clothes, and the plan
 *  layer is the schedule.
 */
export function moveSession(
  week: TrainingWeekRecord,
  sessionId: string,
  to: CalendarDate,
  timestamp: IsoTimestamp,
): Result<TrainingWeekRecord, ValidationIssues> {
  const session = findSession(week, sessionId)
  if (session === undefined) return fail('That session is not part of this week.')
  if (isResolved(session)) {
    return fail('That session has already been recorded, so it cannot be moved.')
  }
  if (!isWithinWeek(week, to)) {
    return fail('A session can only be moved to another day in the same week.')
  }
  if (unwrapDate(to) === unwrapDate(session.scheduledDate)) return ok(week)

  const move: SessionMove = { kind: 'moved', from: session.scheduledDate, to, at: timestamp }
  return ok(
    replaceSession(
      week,
      sessionId,
      (current) => ({ ...current, scheduledDate: to, changes: [...current.changes, move] }),
      timestamp,
    ),
  )
}

export interface OneOffSessionInput {
  readonly domain: FitnessDomain
  readonly name: string
  readonly expectedMinutes?: number
  readonly date: CalendarDate
}

/**
 * Add a session for this week alone (D-105 §4).
 *
 * It counts as recorded training evidence and is labelled as added, so
 * `plannedDate` and `scheduledDate` are both the chosen day — it was never
 * planned for another one. **`sourceSessionId` is absent by definition**: a
 * one-off has no recurring parent, and it is **never written back to the
 * schedule**, which is what keeps "I did something extra on Thursday" from
 * silently becoming "I train on Thursdays".
 */
export function addOneOffSession(
  week: TrainingWeekRecord,
  input: OneOffSessionInput,
  timestamp: IsoTimestamp,
): Result<TrainingWeekRecord, ValidationIssues> {
  const issues = sessionShapeIssues('sessions', input.name, input.expectedMinutes)
  if (issues.length > 0) return err(issues)
  if (!isWithinWeek(week, input.date)) {
    return fail('A one-off session can only be added to a day in the current week.')
  }

  const session: MaterialisedSession = {
    id: newId<'MaterialisedSessionId'>(),
    domain: input.domain,
    name: input.name.trim(),
    ...(input.expectedMinutes === undefined ? {} : { expectedMinutes: input.expectedMinutes }),
    origin: 'oneOff',
    plannedDate: input.date,
    scheduledDate: input.date,
    changes: [],
    createdAt: timestamp,
  }

  return ok({ ...week, sessions: [...week.sessions, session], updatedAt: timestamp })
}

/**
 * Sessions with no outcome, which are *not recorded* rather than missed.
 *
 * Named for what the state is rather than for how a screen might feel about it.
 * There is deliberately no `missedSessions` helper: naming it that way is how a
 * neutral absence becomes a count of failures, and D-105 §5 is explicit that no
 * count of misses may accumulate.
 */
export function unrecordedSessions(week: TrainingWeekRecord): readonly MaterialisedSession[] {
  return week.sessions.filter((session) => !isResolved(session))
}
